import React from 'react';
import { MenuButtonContent } from '../menu-button-content';
import { MenuButtonTrigger } from '../menu-button-trigger';

import { MenuButtonProps } from './types';
import { Popover } from '@redsift/popovers';
import { MenuButtonContext } from './context';
import { FocusWithinGroup, Flexbox, Text, Theme, useTheme, ThemeProvider } from '@redsift/design-system';

const COMPONENT_NAME = 'MenuButton';
const CLASSNAME = 'redsift-menu-button';

/**
 * MenuButton displays a button that opens a menu of actions.
 * Unlike Select (for choosing values), MenuButton triggers actions.
 *
 * Built on Popover with keyboard navigation and proper ARIA roles.
 *
 * @example
 * // Basic menu button
 * <MenuButton label="Actions">
 *   <MenuButton.Trigger>Options</MenuButton.Trigger>
 *   <MenuButton.Content>
 *     <Item onAction={() => handleEdit()}>Edit</Item>
 *     <Item onAction={() => handleDuplicate()}>Duplicate</Item>
 *     <Item onAction={() => handleDelete()}>Delete</Item>
 *   </MenuButton.Content>
 * </MenuButton>
 *
 * @example
 * // Three-dot icon menu (common pattern)
 * <MenuButton>
 *   <MenuButton.Trigger>
 *     <IconButton icon={mdiDotsVertical} aria-label="More options" />
 *   </MenuButton.Trigger>
 *   <MenuButton.Content>
 *     <Item onAction={handleShare}>Share</Item>
 *     <Item onAction={handleExport}>Export</Item>
 *   </MenuButton.Content>
 * </MenuButton>
 *
 * @example
 * // Button with dynamic chevron indicator
 * <MenuButton>
 *   <MenuButton.Trigger>
 *     {({ isOpen }) => (
 *       <Button rightIcon={isOpen ? mdiChevronUp : mdiChevronDown}>
 *         Sort by
 *       </Button>
 *     )}
 *   </MenuButton.Trigger>
 *   <MenuButton.Content>...</MenuButton.Content>
 * </MenuButton>
 *
 * @example
 * // Items with icons
 * <MenuButton>
 *   <MenuButton.Trigger>Actions</MenuButton.Trigger>
 *   <MenuButton.Content>
 *     <Item onAction={handleEdit}>
 *       <Icon icon={mdiPencil} />
 *       Edit
 *     </Item>
 *     <Item onAction={handleDelete}>
 *       <Icon icon={mdiDelete} color="danger" />
 *       Delete
 *     </Item>
 *   </MenuButton.Content>
 * </MenuButton>
 *
 * @example
 * // With links instead of actions
 * <MenuButton>
 *   <MenuButton.Trigger>Navigate</MenuButton.Trigger>
 *   <MenuButton.Content>
 *     <Item><Link href="/dashboard">Dashboard</Link></Item>
 *     <Item><Link href="/settings">Settings</Link></Item>
 *   </MenuButton.Content>
 * </MenuButton>
 */
export const BaseMenuButton: React.FC<MenuButtonProps> & {
  displayName?: string;
  className?: string;
} = (props) => {
  const {
    color,
    description,
    descriptionProps,
    isDisabled,
    label,
    labelProps,
    minWidth = 'trigger-width',
    theme: propsTheme,
    triggerClassName,
    variant,
    wrapperProps,
  } = props;

  const theme = useTheme(propsTheme);

  /** MenuButton context. */
  const state = {
    color,
    isDisabled: isDisabled || false,
    theme,
    triggerClassName,
    variant,
  };

  return (
    <ThemeProvider value={{ theme }}>
      <FocusWithinGroup focusType="virtual-focus" listRole="menu" focusOnInit={false}>
        <MenuButtonContext.Provider value={state}>
          <Flexbox flexDirection="column" alignItems="flex-start" gap="0px" {...wrapperProps}>
            {label && typeof label === 'string' ? <Text {...labelProps}>{label}</Text> : label ? label : null}
            <Popover
              theme={theme}
              overrideDisplayName={{ content: 'MenuButtonContent', trigger: 'MenuButtonTrigger' }}
              placement="bottom-end"
              minWidth={minWidth}
              {...props}
            />
            {description && typeof description === 'string' ? (
              <Text
                theme={theme}
                marginTop="8px"
                variant="caption"
                color={theme === Theme.light ? 'dark-grey' : 'white'}
                {...descriptionProps}
              >
                {description}
              </Text>
            ) : description ? (
              description
            ) : null}
          </Flexbox>
        </MenuButtonContext.Provider>
      </FocusWithinGroup>
    </ThemeProvider>
  );
};
BaseMenuButton.className = CLASSNAME;
BaseMenuButton.displayName = COMPONENT_NAME;

export const MenuButton = Object.assign(BaseMenuButton, {
  Trigger: MenuButtonTrigger,
  Content: MenuButtonContent,
});
